import { Navigate, RouteObject } from 'react-router-dom';
import { WorkspaceErrorElement } from '../components/errors/RouteErrorElement';
import { loadRouteChunk } from '../forge/utils/lazyRouteModule';
import { EvalLayout } from './components/layout/EvalLayout';
import { prCompRoutes } from './pr-compliance/routes';

// Page routes load lazily (route.lazy) so each Eval page is its own chunk and
// the main bundle stays free of Eval page code. The router awaits the chunk
// during navigation, so no Suspense fallback is needed. The `/eval` layout
// element stays eager: it is the shell that has to render while a page chunk
// is still in flight.
export const evalRoutes: RouteObject = {
  path: '/eval',
  element: <EvalLayout />,
  children: [
    {
      // Pathless, so it adds no segment: it exists only to own `errorElement`.
      // Sitting *inside* the layout route means a page crash replaces the
      // content area while the Eval sidebar stays mounted and navigable.
      errorElement: <WorkspaceErrorElement homePath="/eval/runs" homeLabel="Back to runs" />,
      children: [
        { index: true, element: <Navigate to="runs" replace /> },
        {
          path: 'runs',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunListPage'))).RunListPage,
          }),
        },
        {
          path: 'runs/new',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunSchedulerPage')))
              .RunSchedulerPage,
          }),
        },
        {
          path: 'runs/compare',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunsPage')))
              .CompareRunsPage,
          }),
        },
        {
          path: 'runs/compare/errors',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunErrorsPage')))
              .CompareRunErrorsPage,
          }),
        },
        {
          path: 'runs/compare/subtasks',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/CompareRunSubtasksPage')))
              .CompareRunSubtasksPage,
          }),
        },
        {
          path: 'runs/:runId',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunDetailPage')))
              .RunDetailPage,
          }),
        },
        {
          path: 'runs/:runId/errors',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunErrorAnalysisPage')))
              .RunErrorAnalysisPage,
          }),
        },
        {
          path: 'runs/:runId/subtasks',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunSubtaskAnalysisPage')))
              .RunSubtaskAnalysisPage,
          }),
        },
        {
          path: 'runs/:runId/inferences/:runInferenceId',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/runs/RunInferenceSamplePage')))
              .RunInferenceSamplePage,
          }),
        },
        {
          path: 'datasets',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetListPage')))
              .DatasetListPage,
          }),
        },
        {
          path: 'datasets/new',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetCreatePage')))
              .DatasetCreatePage,
          }),
        },
        {
          path: 'datasets/:datasetId',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/datasets/DatasetDetailPage')))
              .DatasetDetailPage,
          }),
        },
        {
          path: 'gt-samples',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/gt-samples/GtSamplesListPage')))
              .GtSamplesListPage,
          }),
        },
        {
          path: 'gt-samples/:gtSampleId',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/gt-samples/GtSamplePage')))
              .GtSamplePage,
          }),
        },
        {
          path: 'import/pr-evaluation-samples',
          lazy: async () => ({
            Component: (
              await loadRouteChunk(() => import('./pages/gt-samples/PrEvaluationImportPage'))
            ).PrEvaluationImportPage,
          }),
        },
        {
          path: 'pr-inferences',
          lazy: async () => ({
            Component: (
              await loadRouteChunk(() => import('./pages/gt-inferences/InferencePickerPage'))
            ).InferencePickerPage,
          }),
        },
        { path: 'models', element: <Navigate to="/eval/od-models" replace /> },
        {
          path: 'od-models',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .OdModelListPage,
          }),
        },
        {
          path: 'pr-models',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .PrModelListPage,
          }),
        },
        {
          path: 'oos-models',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/RayModelListPage')))
              .OosModelListPage,
          }),
        },
        {
          path: 'models/:modelId',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/ModelDetailPage')))
              .ModelDetailPage,
          }),
        },
        {
          path: 'pc-configs',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/PcConfigsListPage')))
              .PcConfigsListPage,
          }),
        },
        {
          path: 'error-labels',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/models/ErrorLabelsPage')))
              .ErrorLabelsPage,
          }),
        },
        {
          path: 'metrics',
          lazy: async () => ({
            Component: (await loadRouteChunk(() => import('./pages/metrics/MetricsPage')))
              .MetricsPage,
          }),
        },
        ...prCompRoutes,
      ],
    },
  ],
};
